import React, { useState, useEffect, useRef, useCallback } from 'react';
import Select from 'react-select';
import type { SingleValue } from 'react-select';
import CorgeeModal from '../Globals/CorgeeModal';
import { attendanceService } from '@services/AttendanceService';
import type { AttendanceData } from '@services/AttendanceService';
import { TimerIcon } from '../../assets/icons/headerIcons';
import '../../assets/fonts/MaterialIcons/material-icons.css';
import './Attendance.css';

interface CommentOption {
  label: string;
  value: string;
}

const FOUR_HOURS = 4 * 60 * 60 * 1000;
const MAX_RETRY_ATTEMPTS = 3;
const RETRY_DELAY = 60 * 1000;
const DEFAULT_DATETIME = '0001-01-01T00:00:00';

const CLOCK_OUT_WINDOW_START = 7 * 60 + 30;
const CLOCK_OUT_WINDOW_END = 9 * 60 + 30;

const isInClockOutRestrictedWindow = (): boolean =>
{
  const now = new Date();
  const totalMinutes = now.getHours() * 60 + now.getMinutes();
  return totalMinutes >= CLOCK_OUT_WINDOW_START && totalMinutes < CLOCK_OUT_WINDOW_END;
};

const getTimeZoneOffset = (): string =>
{
  const now = new Date();
  const offsetMinutes = now.getTimezoneOffset();
  const offsetHours = offsetMinutes / 60;
  const sign = offsetHours < 0 ? '+' : '-';
  const hours = Math.abs(Math.floor(offsetHours));
  return sign + hours.toString();
};

const normalizeDateTime = (dateTimeUTC: string, timeZone: string): string =>
{
  if (dateTimeUTC === DEFAULT_DATETIME)
  {
    return dateTimeUTC;
  }

  if (!dateTimeUTC.includes('Z'))
  {
    dateTimeUTC = dateTimeUTC + 'Z';
  }
  const utcDate = new Date(dateTimeUTC);
  const offsetMs = parseFloat(timeZone) * 60 * 60 * 1000;
  const localDate = new Date(utcDate.getTime() + offsetMs);
  let dateTimeNormalize = localDate.toISOString().replace('Z', '');
  dateTimeNormalize = dateTimeNormalize.replace(/(\.\d{6})\d+/, '$1');
  return dateTimeNormalize;
};

const formatDateRegex = (dateStringUTC: string | undefined, attendanceId: number): string =>
{
  if (dateStringUTC && dateStringUTC !== DEFAULT_DATETIME && attendanceId > 0)
  {
    const cleaned = dateStringUTC.includes('Z') ? dateStringUTC.replace('Z', '') : dateStringUTC;

    const regex = /(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})/;
    const match = cleaned.match(regex);

    if (!match) return 'Invalid Format';

    const [, year, month, day, hours, minutes, seconds] = match;
    return `${month}-${day}-${year} ${hours}:${minutes}:${seconds}`;
  }
  return 'Not registered';
};

const Attendance: React.FC = () =>
{
  const [attendance, setAttendance] = useState<AttendanceData>({} as AttendanceData);
  const [comments, setComments] = useState<CommentOption[]>([]);
  const [validationError, setValidationError] = useState('');
  const [attendanceFeedback, setAttendanceFeedback] = useState('');
  const [selectedComment, setSelectedComment] = useState<CommentOption | null>(null);
  const [otherComment, setOtherComment] = useState('');
  const [disabledButton, setDisabledButton] = useState(false);
  const [isLoadingAttendance, setIsLoadingAttendance] = useState(false);
  const [lastUpdate, setLastUpdate] = useState<number | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const timeoutIdRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const idleCallbackIdRef = useRef<number | null>(null);
  const retryCountRef = useRef(0);
  const lastUpdateRef = useRef<number | null>(null);

  // Keep refs in sync with state for use in event handlers
  useEffect(() =>
  {
    retryCountRef.current = retryCount;
  }, [retryCount]);

  useEffect(() =>
  {
    lastUpdateRef.current = lastUpdate;
  }, [lastUpdate]);

  const cleanupScheduledUpdates = useCallback(() =>
  {
    if (timeoutIdRef.current)
    {
      clearTimeout(timeoutIdRef.current);
      timeoutIdRef.current = null;
    }
    if (idleCallbackIdRef.current && 'cancelIdleCallback' in window)
    {
      cancelIdleCallback(idleCallbackIdRef.current);
      idleCallbackIdRef.current = null;
    }
  }, []);

  const getAttendance = useCallback(async (): Promise<AttendanceData> =>
  {
    const timezone = getTimeZoneOffset();
    const response = await attendanceService.getAttendanceByPersonId(timezone);
    setAttendance(response);
    return response;
  }, []);

  const scheduleNextUpdate = useCallback(
    (updateFn: () => void) =>
    {
      cleanupScheduledUpdates();

      if ('requestIdleCallback' in window)
      {
        idleCallbackIdRef.current = requestIdleCallback(
          () =>
          {
            timeoutIdRef.current = setTimeout(() =>
            {
              updateFn();
            }, FOUR_HOURS);
          },
          { timeout: 2000 },
        );
      }
      else
      {
        timeoutIdRef.current = setTimeout(() =>
        {
          updateFn();
        }, FOUR_HOURS);
      }
    },
    [cleanupScheduledUpdates],
  );

  const updateAttendance = useCallback(async () =>
  {
    if (document.hidden)
    {
      scheduleNextUpdate(updateAttendance);
      return;
    }

    try
    {
      await getAttendance();
      setLastUpdate(Date.now());
      setRetryCount(0);
      scheduleNextUpdate(updateAttendance);
    }
    catch (error)
    {
      console.error('Error getting attendance:', error);

      if (retryCountRef.current < MAX_RETRY_ATTEMPTS)
      {
        setRetryCount(prev => prev + 1);
        setTimeout(() =>
        {
          updateAttendance();
        }, RETRY_DELAY);
      }
      else
      {
        setRetryCount(0);
        scheduleNextUpdate(updateAttendance);
      }
    }
  }, [getAttendance, scheduleNextUpdate]);

  const reloadAttendance = useCallback(() =>
  {
    const localTime = attendance.attendanceDate;
    if (!localTime) return;

    const date = new Date(localTime);
    const dateUTC = new Date(
      Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate() + 1, 5, 59, 0),
    ).toUTCString();
    const dateUTCNow = new Date().toISOString();

    if (new Date(dateUTCNow) > new Date(dateUTC))
    {
      getAttendance();
    }
  }, [attendance.attendanceDate, getAttendance]);

  const handleVisibilityChange = useCallback(() =>
  {
    if (!document.hidden && lastUpdateRef.current)
    {
      const timeSinceLastUpdate = Date.now() - lastUpdateRef.current;

      if (timeSinceLastUpdate >= FOUR_HOURS)
      {
        updateAttendance();
      }
      else
      {
        cleanupScheduledUpdates();
        timeoutIdRef.current = setTimeout(() =>
        {
          updateAttendance();
        }, FOUR_HOURS - timeSinceLastUpdate);
      }
    }
  }, [updateAttendance, cleanupScheduledUpdates]);

  const handleOnline = useCallback(() =>
  {
    if (lastUpdateRef.current)
    {
      const timeSinceLastUpdate = Date.now() - lastUpdateRef.current;
      if (timeSinceLastUpdate >= FOUR_HOURS)
      {
        updateAttendance();
      }
    }
  }, [updateAttendance]);

  const getCommentsList = useCallback(async () =>
  {
    try
    {
      const response = await attendanceService.getCommentsList();
      const objComments: CommentOption[] = response.map(comment => ({
        label: comment,
        value: comment,
      }));
      objComments.push({ label: 'Other', value: 'Other' });
      setComments(objComments);
    }
    catch (error)
    {
      console.error('Error getting comments list:', error);
    }
  }, []);

  // Mount / Unmount
  useEffect(() =>
  {
    const onFocus = () => reloadAttendance();
    const onVisibility = () => handleVisibilityChange();
    const onOnline = () => handleOnline();

    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('online', onOnline);

    getCommentsList();
    updateAttendance();

    return () =>
    {
      cleanupScheduledUpdates();
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('online', onOnline);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openAttendanceModal = async () =>
  {
    setIsModalOpen(true);
    setAttendanceFeedback('');
    setValidationError('');
    setSelectedComment(null);
    setOtherComment('');

    // Disabled until the current attendance is known, to avoid a duplicate clock in/out
    setIsLoadingAttendance(true);
    setDisabledButton(true);

    try
    {
      const response = await getAttendance();

      // Clock out is blocked between 7:30 AM and 9:30 AM
      const hasCheckedIn = response.attendanceId > 0 && response.clockIn !== DEFAULT_DATETIME;
      setDisabledButton(isInClockOutRestrictedWindow() && hasCheckedIn);
    }
    catch (error)
    {
      console.error('Error getting attendance:', error);
      // State unknown: the button stays disabled
      setValidationError('Could not load your attendance. Please close and try again.');
    }
    finally
    {
      setIsLoadingAttendance(false);
    }
  };

  const closeAttendanceModal = () =>
  {
    setIsModalOpen(false);
    // The modal isn't unmounted on close, so an abandoned comment would
    // still be there on the next open
    setSelectedComment(null);
    setOtherComment('');
  };

  const handleSelectComment = (itemSelected: SingleValue<CommentOption>) =>
  {
    setSelectedComment(itemSelected);
    setOtherComment('');
  };

  const handleOtherCommentChange = (event: React.ChangeEvent<HTMLInputElement>) =>
  {
    setOtherComment(event.target.value);
  };

  const submitAttendance = async (typeSubmit: string, currentAttendance: AttendanceData) =>
  {
    setDisabledButton(true);

    if (
      typeSubmit === 'Clock In' &&
      currentAttendance.clockIn !== DEFAULT_DATETIME &&
      currentAttendance.attendanceId > 0
    )
    {
      setAttendanceFeedback('Clock in registered!');
      setSelectedComment(null);
      return;
    }

    if (
      currentAttendance.clockIn !== DEFAULT_DATETIME &&
      currentAttendance.clockOut !== DEFAULT_DATETIME
    )
    {
      setValidationError("Today's attendance registers are already done.");
      setAttendanceFeedback('');
      return;
    }

    if (currentAttendance.requireComment && !selectedComment)
    {
      setValidationError('Comment is required to submit attendance.');
      setAttendanceFeedback('');
      return;
    }

    if (
      selectedComment?.value === 'Other' &&
      currentAttendance.requireComment &&
      !(otherComment.trim().length > 0)
    )
    {
      setValidationError('Enter a valid custom comment.');
      setAttendanceFeedback('');
      return;
    }

    // Clock out is blocked between 7:30 AM and 9:30 AM
    if (typeSubmit === 'Clock Out' && isInClockOutRestrictedWindow())
    {
      setAttendanceFeedback('');
      return;
    }

    setValidationError('');
    setAttendanceFeedback('');

    let comment = '';
    if (currentAttendance.requireComment)
    {
      comment = selectedComment?.value === 'Other' ? otherComment : selectedComment?.value || '';
    }

    const timezone = getTimeZoneOffset();

    try
    {
      const res = await attendanceService.submitAttendanceByPersonId(comment, timezone);
      res.clockIn = normalizeDateTime(res.clockIn, timezone);
      res.clockOut = normalizeDateTime(res.clockOut, timezone);
      setAttendance(res);

      let feedbackMessage = '';
      if (
        res.attendanceId > 0 &&
        (res.clockIn !== DEFAULT_DATETIME || res.clockOut !== DEFAULT_DATETIME)
      )
      {
        const clockedOut = res.clockOut !== DEFAULT_DATETIME;
        const clockedIn = res.clockIn !== DEFAULT_DATETIME;
        feedbackMessage = clockedOut
          ? 'Clock out registered!'
          : clockedIn
            ? 'Clock in registered!'
            : '';
      }

      setAttendanceFeedback(feedbackMessage);
      setSelectedComment(null);
    }
    catch (error)
    {
      console.error('Error submitting attendance:', error);
    }
  };

  const onSubmitAttendance = async (typeSubmit: string) =>
  {
    if (disabledButton) return;
    setDisabledButton(true);

    try
    {
      const response = await getAttendance();
      await submitAttendance(typeSubmit, response);
    }
    catch (error)
    {
      console.error('Error in onSubmitAttendance:', error);
      setDisabledButton(false);
    }
  };

  const isDark = document.documentElement.classList.contains('dark');

  const selectStyles = {
    control: (base: any) => ({
      ...base,
      minHeight: 30,
      fontSize: 13,
      backgroundColor: isDark ? '#3a3c3c' : base.backgroundColor,
      borderColor: isDark ? '#4a4c4c' : '#ccc',
      boxShadow: 'none',
      '&:hover': { borderColor: isDark ? '#62accb' : '#aaa' },
    }),
    valueContainer: (base: any) => ({ ...base, padding: '0 8px' }),
    input: (base: any) => ({
      ...base,
      margin: 0,
      padding: 0,
      color: isDark ? '#a9a093' : base.color,
    }),
    singleValue: (base: any) => ({
      ...base,
      color: isDark ? '#a9a093' : base.color,
    }),
    placeholder: (base: any) => ({
      ...base,
      color: isDark ? '#6e6860' : base.color,
    }),
    option: (base: any, state: any) => ({
      ...base,
      fontSize: 12,
      padding: '5px 12px',
      backgroundColor:
        state.isSelected || state.isFocused ? '#62accb' : isDark ? '#333535' : undefined,
      color: state.isSelected || state.isFocused ? '#fff' : isDark ? '#a9a093' : '#333',
      cursor: 'pointer',
      '&:active': { backgroundColor: '#e05a3e' },
    }),
    menu: (base: any) => ({
      ...base,
      zIndex: 10,
      marginTop: 2,
      backgroundColor: isDark ? '#333535' : base.backgroundColor,
      boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
    }),
    menuList: (base: any) => ({ ...base, padding: 0 }),
    // Must sit above the CorgeeModal backdrop (z-index 1100)
    menuPortal: (base: any) => ({ ...base, zIndex: 999999 }),
  };

  const isClockedIn = attendance.attendanceId > 0;
  const clockAction = isClockedIn ? 'Out' : 'In';
  const bothRegistered =
    attendance.clockIn !== DEFAULT_DATETIME && attendance.clockOut !== DEFAULT_DATETIME;
  const showCommentSelect =
    attendance.requireComment && !bothRegistered && !disabledButton && !isLoadingAttendance;
  const showOtherInput =
    selectedComment?.value === 'Other' &&
    attendance.requireComment &&
    !bothRegistered &&
    !disabledButton &&
    !isLoadingAttendance;

  return (
    <div className="attendanceComponent">
      <button className="mr-[7px]" onClick={openAttendanceModal} title="Attendance">
        <TimerIcon
          className={`w-4 h-4 hover:text-corgee-orange ${isClockedIn ? 'text-corgee-orange' : 'text-corgee-blue'}`}
        />
      </button>

      <CorgeeModal
        className="react-attendance-modal"
        title="Attendance"
        openModal={isModalOpen}
        onClose={closeAttendanceModal}
        onSave={() => onSubmitAttendance(`Clock ${clockAction}`)}
        isDraggable={true}
        closeOnEscape={true}
        textButton={`Clock ${clockAction}`}
        width={350}
        saveButtonColor={disabledButton ? '' : isClockedIn ? '#F26C4F' : '#62accb'}
        saveButtonDisabled={disabledButton || isLoadingAttendance}
        cancelText="Close"
      >
        {isLoadingAttendance && (
          <div className="attendance-loading-container">
            <span className="attendance-loading-spinner" aria-hidden="true"></span>
            <span>Loading attendance...</span>
          </div>
        )}

        <div
          className="attendance-clock-inout-container"
          style={isLoadingAttendance ? { display: 'none' } : undefined}
        >
          <div className="attendance-clock-details-container">
            <span className="attendance-color-clock-in attendance-bold">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="currentColor"
                className="attendance-icon-size"
              >
                <path d="M11 7L9.6 8.4l2.6 2.6H2v2h10.2l-2.6 2.6L11 17l5-5-5-5zm9 12h-8v2h8c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2h-8v2h8v14z" />
              </svg>
            </span>
            <span>{formatDateRegex(attendance.clockIn, attendance.attendanceId)}</span>
          </div>

          <div className="attendance-clock-details-container">
            <span className="attendance-color-clock-out attendance-bold">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="currentColor"
                className="attendance-icon-size"
              >
                <path d="M17 7l-1.4 1.4 2.6 2.6H8v2h10.2l-2.6 2.6L17 17l5-5-5-5zM4 5h8V3H4c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h8v-2H4V5z" />
              </svg>
            </span>
            <span>{formatDateRegex(attendance.clockOut, attendance.attendanceId)}</span>
          </div>
        </div>

        {showCommentSelect && (
          <div className="attendance-modal-input-container">
            <label className="pull-left attendance-input-label">Comment:</label>
            <div
              id="select-render-attendance-comment"
              className="corgee_com_custom-fields-combobox-singleselect input-group attendance-input-font-adjustment"
            >
              <Select<CommentOption>
                autoFocus={false}
                options={comments}
                onChange={handleSelectComment}
                value={selectedComment}
                isClearable={true}
                isMulti={false}
                isSearchable={true}
                openMenuOnFocus={true}
                closeMenuOnSelect={true}
                components={{ DropdownIndicator: () => null, IndicatorSeparator: () => null }}
                placeholder="Select..."
                menuPortalTarget={document.body}
                styles={selectStyles}
                className="input-width"
              />
            </div>
          </div>
        )}

        {showOtherInput && (
          <div className="attendance-modal-input-container">
            <label htmlFor="OtherCommentInput" className="pull-left attendance-input-label">
              Your comment:
            </label>
            <input
              id="OtherCommentInput"
              type="text"
              value={otherComment}
              onChange={handleOtherCommentChange}
              placeholder="Write here"
              className="input-width attendance-other-input-text"
            />
          </div>
        )}

        <div>
          <span className="attendance-color-clock-out attendance-feedback-fontsize">
            {validationError}
          </span>
        </div>

        <div>
          <span className="attendance-color-clock-in attendance-feedback-fontsize">
            {attendanceFeedback}
          </span>
        </div>
      </CorgeeModal>
    </div>
  );
};

export default Attendance;
